import React, { useState, useEffect } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";

import styled from "styled-components";

const PromoCard = styled(Card)`
  height: 100%;
  border: 1px solid #d8d8d8;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  background: #fff;
  overflow: hidden;
  border-radius: 4px;

  &:hover {
    border-color: #bbb;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .card-body {
    display: flex;
    flex-direction: column;
    padding: 14px 16px 12px;
  }
`;

const PromoThumbLink = styled.a`
  display: block;
  overflow: hidden;
  background: #e8e8e8;
  aspect-ratio: 1;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const PromoBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #555;
  background: #e8e8e8;
  padding: 3px 8px;
  border-radius: 3px;
  margin-bottom: 8px;
`;

const PromoTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 6px;

  a {
    color: #333;
    text-decoration: none;
    &:hover {
      color: #66c;
      text-decoration: underline;
    }
  }
`;

const PromoMeta = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 10px;
`;

const PromoTeaser = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: #444;
  margin: 0 0 10px 0;
  flex-grow: 1;
`;

const PromoTags = styled.div`
  font-size: 11px;
  color: #777;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #eee;
`;

const PromoLinks = styled.div`
  font-size: 12px;
  margin-top: 6px;

  a {
    margin-right: 12px;
    white-space: nowrap;
  }
`;

const PromoSectionFooter = styled.div`
  margin-top: 8px;
  font-size: 15px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    color: #333;
    font-weight: 600;
    text-decoration: none;
    &:hover {
      color: #66c;
      text-decoration: underline;
    }
  }

  a svg {
    font-size: 0.95em;
  }
`;

const PROMO_RECENT_URL =
  "//www.dreamchimney.com/api/tracks/message-board/promotracks/mostrecent?tracks=4";

/** Promo listing on the main site */
const PROMO_INDEX_URL = "https://www.dreamchimney.com/promo";

/** Promo detail pages on the main site */
const promoPageUrl = (slug) =>
  slug ? `${PROMO_INDEX_URL}/${slug}` : null;

/** Artwork served under tracks app static path; matches promo track id */
const promoTrackImageUrl = (id) =>
  `//www.dreamchimney.com/tracks/images/promo-tracks/${id}.jpg`;

/** e.g. "2026-04-20" → "April 20, 2026" */
function formatPromoPubDate(pubdate) {
  if (!pubdate || typeof pubdate !== "string") return "";
  const iso = pubdate.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  let date;
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    date = new Date(y, m - 1, d);
  } else {
    date = new Date(pubdate);
  }
  if (Number.isNaN(date.getTime())) return pubdate;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const FeaturedCurrentPromos = () => {
  const [promoTracks, setPromoTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const promo = await axios(PROMO_RECENT_URL);
        const raw = Array.isArray(promo.data) ? promo.data : [];
        setPromoTracks(raw.filter((t) => t && t.id && t.slug));
      } catch (e) {
        setPromoTracks([]);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  return (
    <div className="totd-featured-box">
      <Row className="align-items-start">
        <Col xs={12} className="featured-header" style={{ marginBottom: 16 }}>
          <h2 className="page-header" style={{ marginBottom: 0 }}>
            <FontAwesomeIcon icon="compact-disc" className="compact-disc" />
            Recent Promo Tracks
          </h2>
        </Col>
        {loading &&
          [0, 1, 2, 3].map((i) => (
            <Col xs={12} sm={6} lg={3} key={i} style={{ marginBottom: 16 }}>
              <Skeleton height={220} style={{ marginBottom: 10 }} />
              <Skeleton count={3} />
            </Col>
          ))}
        {!loading && promoTracks.length === 0 && (
          <Col xs={12}>
            <p style={{ marginBottom: 0, color: "#666" }}>No promo tracks at the moment.</p>
          </Col>
        )}
        {!loading &&
          promoTracks.map((track) => {
            const promoHref = promoPageUrl(track.slug);
            const thumbHref = track.sc_url || promoHref;
            return (
              <Col xs={12} sm={6} lg={3} key={track.id} style={{ marginBottom: 16 }}>
                <PromoCard>
                  <PromoThumbLink
                    href={thumbHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={promoTrackImageUrl(track.id)}
                      alt={`${track.artist} — ${track.title}`}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </PromoThumbLink>
                  <Card.Body>
                    <PromoBadge>DC Promo #{track.id}</PromoBadge>
                    <PromoTitle>
                      <a
                        href={promoHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {track.artist} — {track.title}
                      </a>
                    </PromoTitle>
                    <PromoMeta>
                      {track.release ? ` ${track.release} ` : " "}
                      / {track.label}
                      {/* {track.pubdate ? ` · ${formatPromoPubDate(track.pubdate)}` : ""} */}
                    </PromoMeta>
                    {track.teaser ? <PromoTeaser>{track.teaser}</PromoTeaser> : null}
                    {track.tags ? (
                      <PromoTags>Genres: {track.tags}</PromoTags>
                    ) : (
                      <div style={{ flexGrow: 1 }} />
                    )}
                    <PromoLinks>
                      {track.sc_url ? (
                        <a href={track.sc_url} target="_blank" rel="noopener noreferrer">
                          Listen
                        </a>
                      ) : null}
                      {promoHref ? (
                        <a href={promoHref} target="_blank" rel="noopener noreferrer">
                          More info
                        </a>
                      ) : null}
                    </PromoLinks>
                  </Card.Body>
                </PromoCard>
              </Col>
            );
          })}
        {!loading && (
          <Col xs={12}>
            <PromoSectionFooter>
              <a href={PROMO_INDEX_URL} target="_blank" rel="noopener noreferrer">
                More Promo Tracks
                <FontAwesomeIcon icon={faAngleDoubleRight} aria-hidden />
              </a>
            </PromoSectionFooter>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default FeaturedCurrentPromos;
